import React, { useEffect, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { withRouter, Route, Switch } from 'react-router-dom';
import { Select, Button, Loader } from 'sa-ui';
import queryString from 'query-string';

import * as actions from '../../reportsActions';
import * as detailsActions from '../reportApplicationDetailsActions';

import messages from '../reportApplicationDetailsMessages';

import StudentsGradeTable from '../../general-panel/StudentsGradeTable';
import PerformanceStudentsComparative from '../../PerformanceStudentsComparative';
import Chart from '../../ReportBarChart';
import ResultCard from '../../ReportResultCard';
import RankingStudentCard from '../../RankingStudentCard';
import GradePerQuestionTable from '../../GradePerQuestionTable';
import LoadMore from '../../LoadMore';
import QuestionAnalysisTable from '../../QuestionAnalysisTable';
import { promisify } from '../../../app/actions';

import reportMessages from '../../reportsMessages';
import * as helpers from '../../reportHelpers';

import '../../ReportsCommon.scss';

function getRankingLimitFromUrl() {
  const params = queryString.parse(window.location.search.replace('?', ''));
  return params.rankingLimit ? Number(params.rankingLimit) : undefined;
}

const ReportApplicationAssessmentPanelContainer = (props) => {
  const [filter, setFilter] = useState(queryString.parse(window.location.search.replace('?', '')));
  const [rankingPage, setRankingPage] = useState(0);
  const [loading, setLoading] = useState(false);

  function buildRankingParams(idCSchool, idESCourse, page) {
    const rankingLimitFromUrl = getRankingLimitFromUrl();
    return {
      idEvaluation: props.idEvaluation,
      idCSchool,
      idESCourse,
      page,
      ...(rankingLimitFromUrl && { rankingLimit: rankingLimitFromUrl }),
    };
  }

  async function loadMoreRanking() {
    const nextPage = rankingPage + 1;
    setRankingPage(nextPage);
    await props.getESGradeByRankingStudent(
      buildRankingParams(filter.idCSchool, filter.idESCourse, nextPage),
    );
  }

  async function selectFilter(idCSchool, idESCourse) {
    setFilter({ ...filter, idCSchool });
    setLoading(true);
    try {
      await props.getEvaluation({ idEvaluation: props.idEvaluation, idCSchool });
      if (idCSchool) {
        await props.getFilterReports({
          idEvaluation: props.idEvaluation,
          idCSchool,
        });
      }
      await props.getDetailEvaluationAlternatives({ idEvaluation: props.idEvaluation, idESCourse });
      setRankingPage(0);
      await props.getESGradeByRankingStudent(
        buildRankingParams(idCSchool, idESCourse, 0),
      );
      if (idCSchool || idESCourse) {
        await props.getESGradeByGroup({ idEvaluation: props.idEvaluation, idCSchool, idESCourse });
      }
      if (idCSchool && idESCourse) {
        await props.getStudentsAnswersQuestionReport({
          idCSchool,
          idESCourse,
          idEvaluation: props.idEvaluation,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function FilterReports(idEvaluation) {
    setLoading(true);
    try {
      await props.getFilterReports({
        idEvaluation,
      });
    } finally {
      // setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    props.getGroups();
    props.resetAssessmentPanel();
  }, []);

  useEffect(() => {
    if (props.idEvaluation) {
      FilterReports(props.idEvaluation);
    }
  }, [props.idEvaluation]);

  useEffect(() => {
    if (props.idEvaluation || filter.idCSchool || filter.idESCourse) {
      selectFilter(filter.idCSchool, filter.idESCourse);
    }
  }, [props.idEvaluation, filter.idCSchool, filter.idESCourse]);

  useEffect(() => {
    if (props.filterReports) {
      if (props.filterReports.length === 1) {
        setFilter({
          ...filter,
          idCSchool: props.filterReports[0].idCSchool,
        });
      }
    }
  }, [props.filterReports]);

  function clearFilters() {
    setFilter({});
  }

  function handleChangeSchool(event) {
    setFilter({
      ...filter,
      idCSchool: event.target.value,
      idESCourse: undefined,
    });
  }

  function handleChangeCourse(event) {
    setFilter({
      ...filter,
      idESCourse: event.target.value,
    });
  }

  const schoolOptions = helpers.getFASchoolOptions(props.filterReports.schools);
  const classesOptions = helpers.getFClassesOptions(props.filterReports.courses);

  useImperativeHandle(props.forwardRef, () => ({
    getFilter: () => filter,
  }));

  useEffect(() => {
    if (props.gradeByGroup && props.ranking && props.ranking.content && props.ranking.content.length > 0 && props.studentsAnswersQuestionReport.length > 0) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('app-ready')));
    }
  }, [props.gradeByGroup, props.ranking, props.studentsAnswersQuestionReport]);

  return (
    <div>
      <Loader show={loading} />
      <h6 className="filter subtitle mt-3">
        <i className="pe-7s-filter" />
        <FormattedMessage {...messages.filters} />
        <FormattedMessage {...messages.clear}>
          {(txt) => (
            <button className="btn btn-primary btn-sm ml-3" onClick={clearFilters}>
              {txt}
            </button>
          )}
        </FormattedMessage>
      </h6>
      <Row className="align-items-center">
        <Col md={6}>
          <Select
            containerClassName="mt-0"
            nullable
            name="idCSchool"
            onChange={handleChangeSchool}
            value={filter.idCSchool}
            options={schoolOptions}
            placeholder={props.intl.formatMessage(reportMessages.school)}
          />
        </Col>
        <Col md={6}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idCSchool}
            nullable
            name="idCourse"
            value={filter.idCourse}
            onChange={handleChangeCourse}
            options={classesOptions}
            placeholder={props.intl.formatMessage(reportMessages.class)}
          />
        </Col>
      </Row>
      <div id="divToPDF" className="divToPDF">
        <Row className="mt-4">
          <Col md={12}>
            {props.gradeByGroupFailureCode && props.gradeByGroupFailureCode === 39 && (
              <Row className="mb-5">
                <Col md={12}>
                  <div>{props.intl.formatMessage(messages.gradeByGroupError39)}</div>
                </Col>
              </Row>
            )}
          </Col>
          <Col md={12}>
            {props.gradeByGroup && Object.keys(props.gradeByGroup).length > 0 && (
              <Row>
                <Col xs={6} md={6} lg={6} xl={4}>
                  <ResultCard
                    label={props.intl.formatMessage(reportMessages.totalStudents)}
                    value={props.gradeByGroup.totalStudents}
                  />
                </Col>
                <Col xs={6} md={6} lg={6} xl={4}>
                  <ResultCard
                    label={props.intl.formatMessage(reportMessages.averageHits)}
                    value={`${props.gradeByGroup.avgHit || 0}%`}
                  />
                </Col>
                <Col xs={6} md={6} lg={6} xl={4}>
                  <ResultCard
                    label={props.intl.formatMessage(reportMessages.averageErrors)}
                    value={`${props.gradeByGroup.avgErrors || 0}%`}
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        {props.gradeByGroup && Object.keys(props.gradeByGroup).length > 0 && (
          <div>
            <Row className="mt-3">
              <Col md={12}>
                <Chart
                  schools={props.gradeByGroup.group}
                  title={props.intl.formatMessage(messages.performanceComparison)}
                />
              </Col>
            </Row>
          </div>
        )}
        <Row className="mt-3">
          <Col>
            <QuestionAnalysisTable
              data={props.detailEvaluationAlternatives}
              expanded={Boolean(filter.expanded)}
            />
          </Col>
        </Row>
        {props.ranking && props.ranking.content && props.ranking.content.length > 0 && (
          <div>
            <div>
              <h5 className="title">{props.intl.formatMessage(reportMessages.studentsRanking)}</h5>
            </div>
            <div>
              <Row>
                <Col md={12}>
                  {props.ranking.content.map((student) => (
                    <Row className="align-items-center" key={student.position}>
                      <Col style={{ width: 40 }} md="auto">
                        {student.position}º
                      </Col>
                      <Col>
                        <RankingStudentCard
                          initials={student.studentInitials}
                          percentage={student.percentHits}
                          studentName={student.studentName}
                          studentInfo={`${student.courseName} | ${student.schoolName}`}
                          index={student.position}
                        />
                      </Col>
                    </Row>
                  ))}
                  <hr className="my-3" />
                  {rankingPage + 1 < props.ranking.totalpages && (
                    <LoadMore onClick={loadMoreRanking} />
                  )}
                </Col>
              </Row>
            </div>
          </div>
        )}
        {props.studentsAnswersQuestionReport.length > 0 && (
          <Row className="mt-2">
            <Col>
              <GradePerQuestionTable data={props.studentsAnswersQuestionReport} />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

ReportApplicationAssessmentPanelContainer.propTypes = {};

const mapStateToProps = ({ reports, reportApplicationDetails, app }, { match }) => ({
  ...reports,
  ...reportApplicationDetails,
  ...match.params,
  permission: app.permission,
});

export default compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps, promisify({ ...actions, ...detailsActions })),
)(ReportApplicationAssessmentPanelContainer);
